import { getUnprocessableErrors } from '@10xlabs/utils'
import { useMutation } from '@apollo/client'

import { CenteredText } from 'components/Text'
import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { Row } from 'components/v2/Row'
import { updateBusinessCustomerStatus } from 'features/BusinessCustomer/components/BusinessCustomerList/Actions'
import { DEACTIVATE_BUSINESS_CUSTOMER } from 'features/BusinessCustomer/mutations-legacy'
import { ErrorMessage } from 'components/v2/ErrorMessage'

import { ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'
import { GTBusinessCustomer } from 'gql/types.generated'

export const BulkDeactivationModal = ({
  selectedCustomers,
  closeModal,
  onSuccess
}: {
  selectedCustomers: GTBusinessCustomer[]
  closeModal: () => void
  onSuccess: () => void
}): ReactElement => {
  const [deactivateBusinessCustomer, { loading, error }] = useMutation(DEACTIVATE_BUSINESS_CUSTOMER, {
    onCompleted() {
      onSuccess()
    }
  })

  const handleDeactivate = (): void => {
    const mutations = selectedCustomers.map(async (businessCustomer) => {
      return deactivateBusinessCustomer({
        variables: {
          input: { id: businessCustomer.id }
        },
        update(cache) {
          updateBusinessCustomerStatus(businessCustomer.id, cache, false)
        }
      })
    })

    Promise.all(mutations)
  }

  return (
    <Modal.Container>
      <Modal.Header>
        <Heading.H5>
          <FormattedMessage
            id="businessCustomer.bulkDeactivation.title"
            defaultMessage="Deactivate Business Customers"
          />
        </Heading.H5>
      </Modal.Header>
      <Modal.Content>
        <Row>
          <Column>
            <CenteredText>
              <FormattedMessage
                id="businessCustomer.bulkDeactivation.confirmation"
                defaultMessage="Are you sure you want to deactivate {customersCount} {customersCount, plural,
                          one {customer}
                          other {customers}
                        }?"
                values={{
                  customersCount: selectedCustomers.length
                }}
              />
            </CenteredText>
          </Column>
        </Row>
      </Modal.Content>
      <Modal.Footer>
        {error && (
          <Modal.ErrorMessage>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="businessCustomer.bulkDeactivation.error"
                defaultMessage="An error occurred while deactivating the business customers"
              />
            </ErrorMessage>
          </Modal.ErrorMessage>
        )}
        <Modal.Actions>
          <Button type="button" kind="cancel" onClick={(): void => closeModal()}>
            <FormattedMessage id="businessCustomer.bulkDeactivation.actions.close" defaultMessage="No, close" />
          </Button>
          <Button kind="primary" loading={loading} type="button" onClick={(): void => handleDeactivate()}>
            <FormattedMessage id="businessCustomer.bulkDeactivation.action" defaultMessage="Deactivate" />
          </Button>
        </Modal.Actions>
      </Modal.Footer>
    </Modal.Container>
  )
}
